import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const Services = () => {
  const { t } = useTranslation("landing");
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.classList.add("is-visible");
          observer.unobserve(section);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <div id="serv" ref={sectionRef}>
      <div className="container">
        <div className="row d-flex">
          <div className="col-xs-12 col-md-6">
            <div className="about-text">
              <h2>{t("services.title")}</h2>
              <p>{t("services.paragraph")}</p>
            </div>
          </div>
          <div className="col-xs-12 col-md-6 d-flex">
            <img src="img/team.png" className="img-responsive scale-x" alt="" />
          </div>
        </div>
      </div>
    </div>
  );
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export const LdServices = () => {
  const { t, i18n } = useTranslation("landing");
  const gridRef = useRef(null);

  const items = [
    ...asArray(t("ldServices.line", { returnObjects: true })),
    ...asArray(t("ldServices.line2", { returnObjects: true })),
    ...asArray(t("ldServices.line3", { returnObjects: true })),
    ...asArray(t("ldServices.line4", { returnObjects: true })),
  ];

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(".service-card"));
    if (!cards.length) return;

    // Sem IO ou com movimento reduzido, os cards ficam como estão: visíveis.
    if (!("IntersectionObserver" in window)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Só escondemos os cards depois de confirmar que o JS roda e que há quem os
    // revele — assim uma falha de script não apaga o portfólio inteiro.
    grid.classList.add("is-animated");

    // Teto do escalonamento: quem chega direto em /#services vê os 15 cards
    // entrarem no mesmo lote, e sem o teto o último esperaria mais de 1s.
    const MAX_STAGGER_STEPS = 5;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        // Os cards entram em lote (uma linha por vez). O índice dentro do lote
        // dá o escalonamento da esquerda para a direita, seja qual for o número
        // de colunas que o auto-fit escolheu.
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry, indexInBatch) => {
            const step = Math.min(indexInBatch, MAX_STAGGER_STEPS);
            entry.target.style.setProperty("--i", step);
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    cards.forEach((card) => observer.observe(card));
    return () => {
      observer.disconnect();
      grid.classList.remove("is-animated");
    };
  }, [items.length, i18n.language]);

  return (
    <section id="services" className="services text-center">
      <div className="container">
        <div className="section-title services__head">
          <h2>{t("ldServices.title")}</h2>
        </div>

        {items.length === 0 ? (
          <div className="no-services">{t("ldServices.noServices")}</div>
        ) : (
          <div className="services__grid" ref={gridRef}>
            {items.map((d, i) => (
              <article className="service-card" key={i}>
                <span className="service-card__icon">
                  <i className={d.icon} aria-hidden="true" />
                </span>
                <div className="service-desc">
                  <h3>{d.name}</h3>
                  <p>{d.text}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
